export type SupportedLanguage = 'tr' | 'nl';
export const ALLOWED_LANGUAGES: readonly SupportedLanguage[] = ['tr', 'nl'] as const;

export interface VideoThumbnail {
    url: string;
    width: number;
    height: number;
}

export interface VideoThumbnails {
    default?: VideoThumbnail;
    medium?: VideoThumbnail;
    high?: VideoThumbnail;
    standard?: VideoThumbnail;
    maxres?: VideoThumbnail;
}

export interface VideoSnippet {
    title: string;
    description: string;
    channelId: string;
    channelTitle: string;
    thumbnails: VideoThumbnails;
    publishedAt: string;
    defaultAudioLanguage?: string;
    defaultLanguage?: string;
    tags?: string[];
    categoryId?: string;
}

export interface VideoStatus {
    madeForKids?: boolean;
    embeddable?: boolean;
    uploadStatus?: string;
    privacyStatus?: string;
}

export interface VideoContentDetails {
    duration: string;
    dimension?: string;
    definition?: string;
    regionRestriction?: {
        allowed?: string[];
        blocked?: string[];
    };
}

export interface Video {
    id: string;
    snippet: VideoSnippet;
    status?: VideoStatus;
    contentDetails?: VideoContentDetails;
}

export interface FilteredVideo extends Video {
    filterResult: VideoFilterResult;
}

export interface VideoFilterResult {
    allowed: boolean;
    reason?: FilterBlockReason;
    detectedLanguage?: string;
    languageConfidence?: number;
}

export type FilterBlockReason =
    | 'is_short'
    | 'language_not_allowed'
    | 'language_uncertain'
    | 'not_made_for_kids'
    | 'not_embeddable'
    | 'region_blocked'
    | 'denylisted_video'
    | 'denylisted_channel';

export interface VideoCategory {
    id: string;
    nameKey: string;
    nameTr: string;
    nameNl: string;
    searchQueries: { tr: string[]; nl: string[] };
    icon?: string;
}

export interface CategoryRow {
    category: VideoCategory;
    videos: Video[];
}

export interface HomeResponse {
    rows: CategoryRow[];
}

export interface SearchResponse {
    videos: Video[];
    nextPageToken?: string;
}

export interface RelatedResponse {
    videos: Video[];
    currentVideo?: Video | null;
    currentChannel?: { id: string; title: string } | null;
    /** Explicit server verdict for the active route video. */
    playbackVerdict: 'approved' | 'blocked' | 'retryable';
    /** Stable machine-readable reason. Never infer a safety block from an outage. */
    reason?: string;
    retryable?: boolean;
    /** Backward-compatible mirror while older clients are still deployed. */
    safeToPlay: boolean;
}
